import type { CertificationDocument } from 'src/types/CertificationDocumentTypes';
import type {
  IndividualSignatureRequestType,
  UnassignedSignatureDoc,
  SignatureDoc,
  HouseholdMemberOption,
} from 'src/types/SignatureTypes';

/**
 * Sorts signatures into the order a human reads a PDF — top-to-bottom,
 * left-to-right:
 *   1. certification_page.order ascending (page 1 first)
 *   2. y ascending — PDFTron WebViewer uses page coordinates with origin at
 *      the top-left (see SignaturePDFViewer where sig.y is assigned directly
 *      to FreeTextAnnotation.Y), so smaller y means higher on the page.
 *   3. x ascending (left-to-right tiebreaker within a row).
 *
 * To keep visually-aligned boxes on the same row even when their y differs by
 * a pixel or two, rows are formed in a separate pre-grouping pass rather than
 * inside the sort comparator. Putting the tolerance check inside the
 * comparator would make it non-transitive (ENG-2109 Bugbot), because A~B and
 * B~C does not imply A~C. Instead we:
 *
 *   1. Sort by (page, y, x) — a pure, transitive sort.
 *   2. Walk the sorted list per page and bucket rows off the first y of each
 *      row, so a row's boundary can't drift as we add members to it.
 *   3. Sort each row by x.
 *
 * Tolerance is half the smallest box height in the row, which is forgiving of
 * typical hand-placed variation without collapsing distinct rows.
 *
 * The backend returns `needed_signatures` in ISR insertion order, which rarely
 * matches what a compliance user sees on the page. Sorting here keeps the
 * assignment list aligned with the visible boxes in the PDF viewer.
 */
export const sortSignaturesByReadingOrder = <
  T extends Pick<IndividualSignatureRequestType, 'x' | 'y' | 'height'> & {
    certification_page?: { order: number } | null;
  }
>(
  signatures: T[]
): T[] => {
  const byPageThenY = [...signatures].sort((a, b) => {
    const pageA = a.certification_page?.order ?? 0;
    const pageB = b.certification_page?.order ?? 0;
    if (pageA !== pageB) {
      return pageA - pageB;
    }
    const yDiff = Number(a.y) - Number(b.y);
    if (yDiff !== 0) {
      return yDiff;
    }
    return Number(a.x) - Number(b.x);
  });

  const result: T[] = [];
  let currentRow: T[] = [];
  let currentRowPage: number | null = null;
  let currentRowAnchorY: number | null = null;
  let currentRowMinHeight: number = Infinity;

  const flushRow = (): void => {
    if (currentRow.length === 0) {
      return;
    }
    currentRow.sort((a, b) => Number(a.x) - Number(b.x));
    result.push(...currentRow);
    currentRow = [];
    currentRowAnchorY = null;
    currentRowMinHeight = Infinity;
  };

  for (const sig of byPageThenY) {
    const page = sig.certification_page?.order ?? 0;
    const y = Number(sig.y);
    const height = Number(sig.height);

    const isNewPage = currentRowPage !== null && page !== currentRowPage;
    const rowTolerance =
      currentRowMinHeight === Infinity ? 0 : currentRowMinHeight / 2;
    const isNewRow =
      currentRowAnchorY !== null && y - currentRowAnchorY > rowTolerance;

    if (isNewPage || isNewRow) {
      flushRow();
    }

    currentRowPage = page;
    if (currentRowAnchorY === null) {
      currentRowAnchorY = y;
    }
    if (height < currentRowMinHeight) {
      currentRowMinHeight = height;
    }
    currentRow.push(sig);
  }

  flushRow();
  return result;
};

/**
 * Analyzes selected documents for signature requirements.
 * Separates documents into those with unassigned signatures and those with only assigned signatures.
 * Built from scratch for FileManagerV3.
 */
export const analyzeDocumentsForSignatures = (
  docs: CertificationDocument[]
): {
  hasUnassigned: boolean;
  unassignedDocs: UnassignedSignatureDoc[];
  assignedDocs: SignatureDoc[];
  /** Doc IDs that are generating — excluded from payload, shown as skipped. */
  generatingDocIds: Set<number>;
  /** Map of generating doc ID → title for the result dialog. */
  generatingDocTitles: Map<number, string>;
} => {
  const unassignedDocs: UnassignedSignatureDoc[] = [];
  const assignedDocs: SignatureDoc[] = [];
  const generatingDocIds = new Set<number>();
  const generatingDocTitles = new Map<number, string>();
  let hasUnassigned = false;

  for (const doc of docs) {
    if (doc.is_generating || doc.generation_status === 'pending') {
      generatingDocIds.add(doc.id);
      generatingDocTitles.set(doc.id, doc.title || 'Unknown');
      continue;
    }

    const fsr = doc.form_signature_request;

    // Skip if no signature request
    if (!fsr || !fsr.needed_signatures) {
      continue;
    }

    // Filter for valid tenant signatures:
    // - Not already completed
    // - Not compliance signatures
    // - Not already requested
    // - Not date/name/phone/hhmt signatures (only show actual ink/initials signatures)
    const validSignatures = fsr.needed_signatures.filter(
      (sig: IndividualSignatureRequestType) =>
        !sig.signature_completed &&
        !sig.compliance &&
        !fsr.requested &&
        !sig.is_date &&
        !sig.is_name &&
        !sig.is_phone &&
        !sig.is_hhmt
    );

    if (validSignatures.length === 0) {
      continue;
    }

    // Separate into unassigned (no household_member) and assigned, and sort
    // each group in reading order so the assignment list lines up with the
    // boxes rendered in the PDF viewer (ENG-2109).
    const unassignedSignatures = sortSignaturesByReadingOrder(
      validSignatures.filter(
        (sig: IndividualSignatureRequestType) => !sig.household_member
      )
    );
    const assignedSignatures = sortSignaturesByReadingOrder(
      validSignatures.filter(
        (sig: IndividualSignatureRequestType) => sig.household_member
      )
    );

    // If has unassigned signatures, add to unassigned docs
    if (unassignedSignatures.length > 0) {
      hasUnassigned = true;
      unassignedDocs.push({
        document_id: doc.id,
        title: doc.title,
        file_url: doc.file_url || '',
        pages: doc.pages || [],
        annotations_xfdf: doc.annotations_xfdf,
        unassigned_signatures: unassignedSignatures,
        household_members: doc.household_members,
      });
    }

    // If has assigned signatures, add to assigned docs
    if (assignedSignatures.length > 0) {
      assignedDocs.push({
        document_id: doc.id,
        assigned_signatures: assignedSignatures,
      });
    }
  }

  return {
    hasUnassigned,
    unassignedDocs,
    assignedDocs,
    generatingDocIds,
    generatingDocTitles,
  };
};

/**
 * ENG-4206 - true when a document has a requestable tenant signature slot
 * assigned to a household member with no verified contact.
 *
 * Mirrors the slot filters in `analyzeDocumentsForSignatures` so this gate and
 * the dispatcher can never disagree about what is "requestable", and mirrors
 * the backend's `_enrich_docs_with_ulcm_contact` scoping: only members actually
 * assigned to an ISR on THIS document can mark it unverified. Display-only
 * members and unassigned (stub) slots must not block, or the Unassigned
 * Signatures modal could never open.
 *
 * Reachability comes from `fileManagerV3.householdMembers` (fetched from
 * `household/household_member/?certification=<id>`, where
 * `has_valid_communication` is the backend's `has_ulcm_verified_contact`).
 * The document payload carries `household_member.id` - the legacy HHM id when
 * one exists, else the CertificationMember pk - and carries no
 * `cert_member_id`, while the members list carries both. Match on
 * `cert_member_id` when both sides have it, else fall back to `id`: both are
 * produced by the same server-side `migration_legacy_hhm_id or pk` rule.
 */
export const hasUnverifiedRequestableSigner = (
  document: CertificationDocument | null | undefined,
  householdMembers: HouseholdMemberOption[] = []
): boolean => {
  const fsr = document?.form_signature_request;
  if (!fsr || fsr.requested) {
    return false;
  }

  return (fsr.needed_signatures ?? []).some(
    (sig: IndividualSignatureRequestType) => {
      if (sig.compliance || sig.signature_completed) {
        return false;
      }
      if (sig.is_date || sig.is_name || sig.is_phone || sig.is_hhmt) {
        return false;
      }

      const member = sig.household_member;
      if (!member) {
        return false;
      }

      const option = householdMembers.find((m) => {
        if (member.cert_member_id != null && m.cert_member_id != null) {
          return m.cert_member_id === member.cert_member_id;
        }
        return member.id != null && m.id === member.id;
      });

      return option !== undefined && !option.has_valid_communication;
    }
  );
};

/**
 * Builds the payload for the bulk_request endpoint.
 * Transforms assignments and assigned docs into the format expected by the backend.
 * Filters out compliance signatures - only household member signatures should be requested.
 */
export const buildBulkRequestPayload = (
  assignments: Record<number, number>,
  assignedDocs: SignatureDoc[],
  generatingDocIds?: Set<number>
): {
  member_requests: Array<{ isr_id: number }>;
  unassigned_requests: Array<{ isr_id: number; member: number }>;
} => {
  const member_requests: Array<{ isr_id: number }> = [];
  const unassigned_requests: Array<{ isr_id: number; member: number }> = [];
  const genIds = generatingDocIds || new Set<number>();

  // Add all assigned signatures from docs that already had assignments
  // Filter out compliance signatures and generating docs
  for (const doc of assignedDocs) {
    if (genIds.has(doc.document_id)) {
      continue;
    }
    for (const sig of doc.assigned_signatures) {
      if (!sig.compliance) {
        member_requests.push({ isr_id: sig.id });
      }
    }
  }

  // Add newly assigned signatures from the assignment modal
  for (const [isrIdStr, memberId] of Object.entries(assignments)) {
    const isrId = parseInt(isrIdStr, 10);
    unassigned_requests.push({
      isr_id: isrId,
      member: memberId,
    });
  }

  return {
    member_requests,
    unassigned_requests,
  };
};

/**
 * Validates that each document with unassigned signatures has at least 1 ISR assigned.
 * Not every ISR needs assignment, just minimum 1 per document.
 */
export const allDocumentsHaveMinimumAssignments = (
  unassignedDocs: UnassignedSignatureDoc[],
  assignments: Record<number, number>
): boolean => {
  // For each document with unassigned signatures
  for (const doc of unassignedDocs) {
    // Check if at least one ISR from this document has an assignment
    const hasAtLeastOne = doc.unassigned_signatures.some((sig) =>
      Boolean(assignments[sig.id])
    );

    // If this document doesn't have at least 1 assigned, validation fails
    if (!hasAtLeastOne) {
      return false;
    }
  }

  // All documents have at least 1 assigned
  return true;
};

/**
 * Gets a display label for a signature based on its type.
 */
export const getSignatureLabel = (
  sig: IndividualSignatureRequestType
): string => {
  if (sig.is_name) {
    return 'Name';
  }
  if (sig.is_date) {
    return 'Date';
  }
  if (sig.is_hhmt) {
    return 'Household Member Type';
  }
  if (sig.is_phone) {
    return 'Phone';
  }
  if (sig.is_initials) {
    return 'Initials';
  }
  return 'Signature';
};
